import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Globe,
  Radio,
  RefreshCw,
  AlertCircle,
  Activity,
  Users,
  Calendar
} from 'lucide-react';
import { LoadingSpinner } from 'components/common';
import api from '../../../../services/api';

/**
 * Extrai do array retornado pelo webhook o bloco com métricas de LOS
 * (quantidade_clientes, quantidade_clientes_mes, etc.).
 */
function extrairBlocoAlertaLos(dashboardData) {
  if (!Array.isArray(dashboardData)) return null;
  return (
    dashboardData.find(
      (item) =>
        item &&
        typeof item === 'object' &&
        (Object.prototype.hasOwnProperty.call(item, 'quantidade_clientes') ||
          Object.prototype.hasOwnProperty.call(item, 'quantidade_clientes_mes'))
    ) || null
  );
}

const ReelunetAlertaLosDashboard = ({ action = 'reelunet-selected', refreshKey = 0 }) => {
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchDashboardData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get('/automacoes/dashboard', {
        params: { action }
      });
      const payload = response?.data?.data ?? response.data;
      setDashboardData(payload);
    } catch (err) {
      console.error('Erro ao carregar Alerta de LOS Reelunet:', err);
      setError(err.response?.data?.message || err.message || 'Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  }, [action]);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData, refreshKey]);

  const blocoLos = useMemo(() => extrairBlocoAlertaLos(dashboardData), [dashboardData]);

  const quantidadeClientes = useMemo(() => {
    const raw = blocoLos?.quantidade_clientes;
    if (raw === undefined || raw === null || raw === '') return null;
    const n = Number(raw);
    return Number.isFinite(n) ? n : String(raw);
  }, [blocoLos]);

  const quantidadeClientesMes = useMemo(() => {
    const raw = blocoLos?.quantidade_clientes_mes;
    if (raw === undefined || raw === null || raw === '') return null;
    const n = Number(raw);
    return Number.isFinite(n) ? n : String(raw);
  }, [blocoLos]);

  if (loading) {
    return (
      <LoadingSpinner
        fullScreen={false}
        className="min-h-[240px] w-full"
        cardClassName="max-w-md w-full"
        label="Carregando Alerta de LOS"
        subtitle="Aguarde enquanto atualizamos os dados."
      />
    );
  }

  if (error) {
    return (
      <div className="text-center py-8">
        <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-[color:var(--text-primary)] mb-2">Erro ao carregar dados</h3>
        <p className="text-[color:var(--text-muted)] mb-4">{error}</p>
        <button
          onClick={fetchDashboardData}
          className="inline-flex items-center px-4 py-2 bg-[color:var(--accent)] text-white rounded-md hover:opacity-90"
        >
          <RefreshCw className="h-4 w-4 mr-2" />
          Tentar novamente
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-[color:var(--surface)] rounded-lg shadow-sm border border-[color:var(--border)] p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-[color:var(--surface-muted)] rounded-lg">
              <Globe className="h-6 w-6 text-green-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-[color:var(--text-primary)]">Reelunet</h1>
              <p className="text-[color:var(--text-muted)]">
                Dashboard de automações da Reelunet — Alerta de LOS ativo
              </p>
            </div>
          </div>
          <div className="p-2 bg-[color:var(--surface-muted)] rounded-lg hidden sm:block">
            <Radio className="h-6 w-6 text-[color:var(--accent)]" />
          </div>
        </div>
      </div>

      <div className="mb-8">
        <h3 className="text-lg font-semibold text-[color:var(--text-primary)] mb-4 flex items-center">
          <Activity className="h-5 w-5 mr-2 text-blue-600" />
          Alerta de LOS ativo
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="bg-[color:var(--surface)] p-6 rounded-lg border border-[color:var(--border)]">
            <div className="flex items-center">
              <div className="p-2 bg-[color:var(--surface-muted)] rounded-lg">
                <Users className="h-6 w-6 text-blue-600" />
              </div>
              <div className="ml-4 min-w-0">
                <p className="text-sm font-medium text-[color:var(--text-muted)]">
                  Clientes notificados por LOS
                </p>
                <p className="text-2xl font-bold text-[color:var(--text-primary)] tabular-nums">
                  {quantidadeClientes !== null ? quantidadeClientes : '—'}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-[color:var(--surface)] p-6 rounded-lg border border-[color:var(--border)]">
            <div className="flex items-center">
              <div className="p-2 bg-[color:var(--surface-muted)] rounded-lg">
                <Calendar className="h-6 w-6 text-emerald-600" />
              </div>
              <div className="ml-4 min-w-0">
                <p className="text-sm font-medium text-[color:var(--text-muted)]">
                  Notificações mensais
                </p>
                <p className="text-2xl font-bold text-[color:var(--text-primary)] tabular-nums">
                  {quantidadeClientesMes !== null ? quantidadeClientesMes : '—'}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReelunetAlertaLosDashboard;
